import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  ArrowLeft01Icon, 
  UserCircleIcon, 
  Mail01Icon, 
  SmartPhone01Icon, 
  Clock01Icon,
  Delete02Icon,
  Copy01Icon
} from '@hugeicons/core-free-icons';
import { api } from '../../utils/api';
import { FadeLoader } from 'react-spinners';
import Button from '../../components/Button';
import { useToast } from '../../contexts/ToastContext';

const AdminUserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/users/${userId}`);
      setData(res.data);
    } catch (err) {
      console.error("Failed to fetch user:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const handleUnlink = async () => {
    if (!window.confirm("Are you sure you want to unlink this user's Deriv account? This will disable their active connection and clear their API token.")) return;
    
    setIsUnlinking(true);
    try {
      await api.post(`/admin/users/${userId}/unlink-deriv`);
      addToast("Deriv account unlinked successfully", "success");
      fetchUser(); // Refresh data
    } catch (err) {
      addToast(err.message || "Failed to unlink account", "error");
    } finally {
      setIsUnlinking(false);
    }
  };

  if (loading && !data) return <div className="min-h-screen flex items-center justify-center"><FadeLoader color="#4ade80" /></div>;
  if (!data) return <div className="p-20 text-center font-black uppercase">User Not Found</div>;

  const { user, recent_transactions } = data;

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-20 px-4 sm:px-8 pt-6">
      <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-muted-gray hover:text-charcoal dark:hover:text-white transition-colors font-black text-sm uppercase tracking-widest">
        <HugeiconsIcon icon={ArrowLeft01Icon} size={18} /> Back to Admin
      </button>

      <div className="bg-white dark:bg-[#111] p-8 rounded-[3rem] border border-gray-100 dark:border-white/10 shadow-sm flex flex-col md:flex-row gap-8 items-center md:items-start">
        <div className="w-32 h-32 rounded-full bg-soft-green/10 flex items-center justify-center text-soft-green">
          <HugeiconsIcon icon={UserCircleIcon} size={64} />
        </div>
        <div className="flex-1 text-center md:text-left min-w-0 w-full">
          <h1 className="text-2xl sm:text-4xl font-black text-charcoal dark:text-white tracking-tight mb-4 break-all">
            {user.business_name || user.name || 'Anonymous User'}
          </h1>
          <div className="flex flex-wrap justify-center md:justify-start gap-3">
            {user.roles?.map(r => (
              <span key={r.id} className="px-3 py-1 bg-black text-white dark:bg-white dark:text-black rounded-full text-[10px] font-black uppercase tracking-widest">{r.name}</span>
            ))}
            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${user.email_verified_at ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
              {user.email_verified_at ? 'Verified Email' : 'Unverified'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Contact Info */}
        <div className="bg-white dark:bg-[#111] p-8 rounded-[2.5rem] border border-gray-100 dark:border-white/10 shadow-sm space-y-6">
          <h3 className="text-xl font-black">Contact & Identity</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-white/5 rounded-2xl group">
              <div className="flex items-center gap-4">
                <HugeiconsIcon icon={UserCircleIcon} className="text-muted-gray" />
                <div><p className="text-[10px] font-black uppercase text-muted-gray">User UID</p><p className="font-mono text-[10px]">{user.uid}</p></div>
              </div>
              <button onClick={() => { navigator.clipboard.writeText(user.uid); addToast("UID Copied!", "success"); }} className="p-2 text-soft-green opacity-0 group-hover:opacity-100 transition-all hover:scale-110">
                <HugeiconsIcon icon={Copy01Icon} size={16} />
              </button>
            </div>
            <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-white/5 rounded-2xl">
              <HugeiconsIcon icon={Mail01Icon} className="text-muted-gray" />
              <div><p className="text-[10px] font-black uppercase text-muted-gray">Email Address</p><p className="font-bold">{user.email}</p></div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-white/5 rounded-2xl">
              <HugeiconsIcon icon={SmartPhone01Icon} className="text-muted-gray" />
              <div>
                <p className="text-[10px] font-black uppercase text-muted-gray">Phone Number</p>
                <p className="font-bold">
                  {user.primary_phone?.full_phone_number || 
                   user.primaryPhone?.full_phone_number || 
                   user.phone_numbers?.[0]?.full_phone_number || 
                   user.phoneNumbers?.[0]?.full_phone_number || 
                   'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Deriv Accounts */}
        <div className="bg-white dark:bg-[#111] p-8 rounded-[2.5rem] border border-gray-100 dark:border-white/10 shadow-sm space-y-6">
          <h3 className="text-xl font-black">Linked Deriv Accounts</h3>
          {user.deriv_accounts?.length > 0 ? (
            <div className="space-y-4">
              {user.deriv_accounts.map(acc => (
                <div key={acc.id} className="p-4 bg-soft-green/5 border border-soft-green/10 rounded-2xl flex justify-between items-center">
                  <div><p className="text-[10px] font-black uppercase text-soft-green">CR Number</p><p className="font-black text-xl">{acc.deriv_id}</p></div>
                  <div className="text-right"><p className="text-[10px] font-black uppercase text-muted-gray">Balance</p><p className="font-bold">${acc.current_balance}</p></div>
                </div>
              ))}
              <div className="pt-2">
                <Button 
                  onClick={handleUnlink} 
                  disabled={isUnlinking} 
                  variant="danger" 
                  className="w-full py-3 rounded-2xl font-black text-xs flex items-center justify-center gap-2"
                >
                  <HugeiconsIcon icon={Delete02Icon} size={16} />
                  {isUnlinking ? 'Clearing Connection...' : 'Unlink Deriv Accounts'}
                </Button>
                <p className="text-[9px] text-muted-gray text-center mt-3 uppercase font-bold tracking-tighter opacity-60">
                  This will set active status to false and clear the stored API token.
                </p>
              </div>
            </div>
          ) : <p className="text-muted-gray italic">No Deriv accounts linked yet.</p>}
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-white dark:bg-[#111] rounded-[2.5rem] border border-gray-100 dark:border-white/10 overflow-hidden shadow-sm">
        <div className="p-8 border-b border-gray-100 dark:border-white/10 flex justify-between items-center">
          <h3 className="text-xl font-black">Recent Transactions</h3>
          <HugeiconsIcon icon={Clock01Icon} className="text-muted-gray" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 dark:bg-white/5 text-[10px] font-black uppercase tracking-widest text-muted-gray">
              <tr>
                <th className="px-8 py-4">Ref</th>
                <th className="px-8 py-4">Type</th>
                <th className="px-8 py-4 text-right">Amount USD</th>
                <th className="px-8 py-4 text-right">Amount NGN</th>
                <th className="px-8 py-4 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-white/10">
              {recent_transactions.map(tx => {
                const rate = parseFloat(tx.order?.current_rate || 1);
                const amountNgn = parseFloat(tx.amount_sent || 0);
                const amountUsd = tx.amount_usd ? parseFloat(tx.amount_usd) : (rate > 0 ? amountNgn / rate : 0);

                return (
                  <tr 
                    key={tx.uid} 
                    onClick={() => navigate(`/admin/orders/${tx.uid}`)}
                    className="hover:bg-gray-50 dark:hover:bg-white/5 transition-colors cursor-pointer"
                  >
                    <td className="px-8 py-5 font-mono text-xs text-muted-gray">{tx.reference}</td>
                    <td className="px-8 py-5 uppercase font-black text-[10px]">{tx.type}</td>
                    <td className="px-8 py-5 text-right font-black">
                      ${amountUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-8 py-5 text-right font-black text-soft-green">₦{amountNgn.toLocaleString()}</td>
                    <td className="px-8 py-5 text-center">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${tx.status === 'completed' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}`}>{tx.status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {recent_transactions.length === 0 && <div className="p-12 text-center text-muted-gray italic">No transactions found for this user.</div>}
        </div>
      </div>
    </div>
  );
};

export default AdminUserDetail;